import { apiClient } from '../utils/api-client';
import type { CragReport, CreateCragReportData } from '../types/crag-report';

interface CragReportResource {
  id: string;
  type: 'crag_report';
  attributes: CragReport;
}

interface CragReportsResponse {
  data: CragReportResource[];
  /** Whether the reader may accept and close reports here — see the API. */
  meta?: { can_manage?: boolean };
}

interface CragReportResponse {
  data: CragReportResource;
}

/** What `list` answers with: the reports, plus what the reader may do to them. */
export interface CragReportsResult {
  reports: CragReport[];
  canManage: boolean;
}

/**
 * Reports on one crag. The same endpoint serves the crag page and the panel's
 * queue — the API decides which rows the caller is allowed to see, so there is
 * one list rather than two that can disagree.
 */
class CragReportsService {
  async list(cragSlug: string): Promise<CragReportsResult> {
    const response = await apiClient.get(`/api/crags/${cragSlug}/reports`);

    if (!response.ok) {
      throw new Error('Failed to fetch crag reports');
    }

    const data: CragReportsResponse = await response.json();
    return {
      reports: data.data.map((item) => item.attributes),
      canManage: data.meta?.can_manage ?? false,
    };
  }

  async create(cragSlug: string, payload: CreateCragReportData): Promise<CragReport> {
    const response = await apiClient.post(`/api/crags/${cragSlug}/reports`, {
      data: { attributes: payload },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.errors?.[0]?.detail || 'Failed to create report');
    }

    const data: CragReportResponse = await response.json();
    return data.data.attributes;
  }

  /** Vouching for a report — this is what mails the crag's maintainers. */
  async accept(cragSlug: string, reportId: string): Promise<CragReport> {
    return this.transition(cragSlug, reportId, 'accept');
  }

  async reject(cragSlug: string, reportId: string): Promise<CragReport> {
    return this.transition(cragSlug, reportId, 'reject');
  }

  async resolve(cragSlug: string, reportId: string, resolutionMessage?: string): Promise<CragReport> {
    return this.transition(cragSlug, reportId, 'resolve', {
      resolution_message: resolutionMessage ?? null,
    });
  }

  private async transition(
    cragSlug: string,
    reportId: string,
    action: 'accept' | 'reject' | 'resolve',
    attributes: Record<string, unknown> = {}
  ): Promise<CragReport> {
    const response = await apiClient.post(`/api/crags/${cragSlug}/reports/${reportId}/${action}`, {
      data: { attributes },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.errors?.[0]?.detail || `Failed to ${action} report`);
    }

    const data: CragReportResponse = await response.json();
    return data.data.attributes;
  }
}

export const cragReportsService = new CragReportsService();
